import * as pc from 'playcanvas'
import { INPUT_PRIORITY, modalInputRouter } from '../../input/modalInputRouter'
import { useArenaStore } from '../../store'
import { createWorldOverlay, type OverlayHandle } from './interiorShared'
import {
  activateHqRoomAction,
  hqRoomActionAt,
  type HqRoomAction,
} from './hqRoomActions'

/** One shared proximity prompt and input owner for all operational HQ rooms. */
export class HqRoomActionSystem {
  private camera!: pc.Entity
  private root!: pc.Entity
  private overlay: OverlayHandle | null = null
  private prompt!: HTMLDivElement
  private keycap!: HTMLSpanElement
  private copy!: HTMLSpanElement
  private currentAction: HqRoomAction | null = null
  private active = false
  private disposeInput?: () => void

  init(app: pc.Application, root: pc.Entity, camera: pc.Entity): void {
    this.root = root
    this.camera = camera
    this.overlay = createWorldOverlay(app, { display: 'none', zIndex: '8' })
    this.prompt = document.createElement('div')
    this.prompt.dataset.testid = 'hq-room-action'
    this.prompt.setAttribute('role', 'status')
    this.prompt.setAttribute('aria-live', 'polite')
    Object.assign(this.prompt.style, {
      position: 'absolute', left: '50%', bottom: '4.5rem',
      transform: 'translateX(-50%)', display: 'none', alignItems: 'center',
      padding: '9px 13px', borderRadius: '999px',
      background: 'rgba(7,14,23,.92)', border: '1px solid rgba(125,211,252,.55)',
      color: '#e8f7ff', font: '700 12px/1.2 Nunito, system-ui, sans-serif',
      letterSpacing: '.04em', boxShadow: '0 10px 32px rgba(0,0,0,.35)',
    })
    this.keycap = document.createElement('span')
    this.keycap.textContent = 'E'
    this.keycap.setAttribute('aria-hidden', 'true')
    Object.assign(this.keycap.style, {
      display: 'inline-grid', placeItems: 'center', minWidth: '20px',
      height: '20px', marginRight: '7px',
      border: '1px solid rgba(255,255,255,.35)', borderRadius: '5px',
    })
    this.copy = document.createElement('span')
    this.prompt.append(this.keycap, this.copy)
    this.overlay.element.appendChild(this.prompt)
    this.disposeInput = modalInputRouter.register(INPUT_PRIORITY.room, this.onKeyDown)
  }

  private readonly onKeyDown = (event: KeyboardEvent): boolean => {
    if (!this.active) return false
    return activateHqRoomAction(
      event.code,
      this.currentAction,
      useArenaStore.getState(),
      document.exitPointerLock?.bind(document),
    )
  }

  frame(active: boolean): void {
    this.active = active
    if (!this.overlay) return
    this.overlay.element.style.display = active ? 'block' : 'none'
    const state = useArenaStore.getState()
    if (!active || !state.isPlaying || state.isPaused || state.isAgentTalkOpen ||
      state.isWorkplacePhoneOpen || state.activeInteraction !== null ||
      state.inspectorRunId !== null) {
      this.currentAction = null
      this.prompt.style.display = 'none'
      return
    }
    const camera = this.camera.getPosition()
    const origin = this.root.getPosition()
    this.currentAction = hqRoomActionAt(
      camera.x - origin.x,
      camera.y - origin.y,
      camera.z - origin.z,
    )
    this.prompt.style.display = this.currentAction ? 'flex' : 'none'
    if (this.currentAction) {
      this.copy.textContent = `${this.currentAction.roomLabel} · ${this.currentAction.label}`
      this.prompt.setAttribute(
        'aria-label',
        `${this.currentAction.roomLabel}: press E to ${this.currentAction.label.toLowerCase()}`,
      )
    } else {
      this.prompt.removeAttribute('aria-label')
    }
  }

  destroy(): void {
    this.disposeInput?.()
    this.disposeInput = undefined
    this.overlay?.dispose()
    this.overlay = null
  }
}
